/*
  Fast Simon storefront customization for Valhalla Tactical — behaviour layer.

  Loaded by snippets/FSHooks.liquid, which stays inline because it carries the values only
  Liquid can produce (window.FS_DATA) and __fast_options, which the site-wide autocomplete
  reads at init. This file holds no Liquid, so it is served as a cached static asset instead
  of being re-sent inside every HTML document.

  Wrapped in an IIFE: the previous inline version published 51 names on window, including
  generic ones (guard, hooks, productHandle, closeSearchDrawer) that a theme or app script
  could collide with — and a later `const hooks` anywhere on the page threw SyntaxError.
  The only globals published now are the three registration latches and the wishlist app's
  own contract (window.wishlistBtns, collectionWlBtnTarget, searchWishlist).
*/
(function () {
    'use strict';

    /* Start Theme Bridge */

    /* Everything Liquid had to build is handed over by the FSHooks shim. Defaults keep this
       file harmless if it is ever loaded without it. */
    var FS_DATA = window.FS_DATA || {};

    /* Option names live treats as colour swatches. Bundles name the option after the
       bundled item ("… Wrist Pouch (Colour)"), so live shows no swatches for them while
       Fast Simon does — hasThemeColorOption() re-applies live's rule. */
    var THEME_COLOR_OPTION_IDS = FS_DATA.colorOptionIds || '';

    var QUICK_BUY_TEMPLATE = FS_DATA.quickBuyTemplate || '';

    /* same translations the theme's own /search page uses */
    var SEARCH_FOR_LABEL = FS_DATA.searchForLabel || '';
    var SEARCH_LABEL = FS_DATA.searchLabel || '';

    /* End Theme Bridge */

    /* Start Shared Helpers */

    /* Both surfaces render the same card, under different class names. */
    var CARD_SELECTOR = '.fs-results-product-card, .fs-upsell-cross-sell-product';

    /* PLP cards carry data-product-id, upsell slides carry data-id. */
    function cardProductId(productElement) {
        return productElement.dataset.productId || productElement.dataset.id || '';
    }

    function getProductElements(element) {
        return [...element.querySelectorAll('.fs-results-product-card')];
    }

    /* attributes arrive as [["Vendor", ["Garmont"]], ...] when with_product_attributes is on */
    function getProductAttribute(data, key) {
        var pair = ((data && data.attributes) || []).find(function (a) { return a[0] === key; });
        return pair && pair[1] && pair[1][0];
    }

    /* this catalogue uses the British spelling on the variant option */
    function getVariantColor(variant) {
        var attributes = variant && variant.attributes;
        return attributes && (attributes.Colour || attributes.Color);
    }

    /* Hook bodies run inside Fast Simon's render cycle: a throw here aborts that render,
       leaving the SERP stuck in .fs-results-is-loading with pointer-events:none. Contain
       our own failures so one bad product cannot freeze the page. */
    function guard(label, fn) {
        try { fn(); }
        catch (error) {
            if (window.console && console.warn) console.warn('[VT] ' + label + ' failed:', error);
        }
    }

    /* End Shared Helpers */

    /* Start Colour Swatches (pattern files) */

    /* Fast Simon renders a plain colour chip; the live theme paints each one with an
       uploaded pattern file. Built as the Fast Simon "Color Swatches With a Pattern File"
       example does it — set the file as the swatch's background from the serp-filters and
       serp-product-grid hooks — with one adjustment: that example derives the file name
       from the colour name, which resolves 5 of this catalogue's 73 colours because the
       CDN is case-sensitive and the extension varies per file. The real names come from
       Liquid instead (see FSSwatchEntry). */

    /* Colour name (lower-cased) -> file path, whole-catalogue. */
    var SWATCH_FILES = FS_DATA.swatchFiles || {};

    /* Root-relative, so the storefront's own origin serves it on any domain. */
    var SWATCH_BASE = '/cdn/shop/files/';

    /* Colour name (lower-cased) -> {i: files path} | {u: image URL} | {c: [colours]}, from the
       store's Color metaobjects (FSSwatchRegistry). It only fills gaps: SWATCH_FILES is
       checked first, so every swatch drawn before the registry existed is drawn the same.
       Its own JSON element, so a Liquid error in it leaves the files map working alone. */
    var SWATCH_REGISTRY = readSwatchRegistry();

    function readSwatchRegistry() {
        var node = document.getElementById('vt-swatch-registry');
        if (!node) return {};
        try {
            var data = JSON.parse(node.textContent);
            if (!data || typeof data !== 'object') return {};

            /* above 250 entries Liquid can only hand over one page of them */
            var emitted = Object.keys(data).filter(function (key) { return key.charAt(0) !== '_'; }).length;
            if (data._count > 250 && window.console && console.warn) {
                console.warn('[VT] swatch registry holds ' + emitted + ' of ' + data._count + ' Color entries');
            }
            return data;
        } catch (error) {
            if (window.console && console.warn) console.warn('[VT] swatch registry unreadable, using files only:', error);
            return {};
        }
    }

    function swatchKey(colorName) {
        return String(colorName).trim().toLowerCase();
    }

    function registryEntry(colorName) {
        var key = swatchKey(colorName);
        /* never a "_v"/"_count" bookkeeping field, and never an inherited property */
        if (key.charAt(0) === '_' || !Object.prototype.hasOwnProperty.call(SWATCH_REGISTRY, key)) return null;
        var entry = SWATCH_REGISTRY[key];
        return entry && typeof entry === 'object' ? entry : null;
    }

    function swatchImageUrl(colorName) {
        var file = SWATCH_FILES[swatchKey(colorName)];
        if (file) return SWATCH_BASE + file;

        var entry = registryEntry(colorName);
        if (!entry) return '';
        if (typeof entry.i === 'string' && entry.i) return SWATCH_BASE + entry.i;
        if (typeof entry.u === 'string' && entry.u) return entry.u;
        return '';
    }

    /* Only values the browser accepts as a colour, so a bad entry paints nothing rather
       than a broken background declaration. */
    function swatchColors(colorName) {
        var entry = registryEntry(colorName);
        if (!entry || !Array.isArray(entry.c)) return [];
        return entry.c.filter(function (value) {
            return typeof value === 'string' && value &&
                (!window.CSS || !CSS.supports || CSS.supports('color', value));
        });
    }

    /* Two or more colours split the swatch into equal hard-edged bands, the way the admin
       draws a two-tone entry such as "Black and Yellow". */
    function splitSwatch(colors) {
        var step = 100 / colors.length;
        var stops = colors.map(function (color, index) {
            return color + ' ' + (index * step) + '% ' + ((index + 1) * step) + '%';
        });
        return 'linear-gradient(90deg, ' + stops.join(', ') + ')';
    }

    /* Image first, then the entry's colour(s); a colour with neither keeps the empty ring it
       gets on the live theme. Rows are reused for other colours (see paintFilterOption), so
       every call rewrites both properties. Fast Simon may fill a swatch inline itself, so
       its value is remembered before a registry colour replaces it and put back afterwards —
       never blanked. */
    function paintSwatch(swatch, colorName) {
        if (!swatch) return;
        var url = swatchImageUrl(colorName);
        var colors = url ? [] : swatchColors(colorName);

        if (url) swatch.style.backgroundImage = 'url("' + url + '")';
        else if (colors.length > 1) swatch.style.backgroundImage = splitSwatch(colors);
        else swatch.style.backgroundImage = '';

        if (colors.length === 1) {
            if (!('vtFillWas' in swatch.dataset)) swatch.dataset.vtFillWas = swatch.style.backgroundColor;
            swatch.style.backgroundColor = colors[0];
        } else if ('vtFillWas' in swatch.dataset) {
            swatch.style.backgroundColor = swatch.dataset.vtFillWas;
            delete swatch.dataset.vtFillWas;
        }
    }

    /* Uses Fast Simon's own swatch classes so dashboard colour mapping still drives the fill.

       Keyed on the colour the row shows now, not on "this row was done once": the group's
       search box filters the list in place, and Fast Simon does not rebuild the rows for
       it — it reuses the first N rows and swaps only their text. A once-per-row flag left
       each reused row wearing the swatch of the colour it used to hold ("TDU Green" with the
       Black swatch), and rows added back when the search is cleared got none at all. */
    function paintFilterOption(option) {
        var name = option.querySelector('.fs-serp-filter-text');
        var checkmark = option.querySelector('.fs-serp-checkmark');
        if (!name || !checkmark) return;

        var colorName = name.textContent.trim();
        var swatch = option.querySelector('.vt-filter-swatch');
        if (swatch && swatch.dataset.vtColor === colorName) return;

        if (!swatch) {
            swatch = document.createElement('span');
            checkmark.insertAdjacentElement('afterend', swatch);
        }

        var slug = colorName.toLowerCase().replace(/[^a-z0-9]/g, '');
        swatch.className = 'vt-filter-swatch color-swatch fast-swatch-color-fallback' +
            ' fast-swatch-color-' + slug + ' isp-product-color-swatch-' + slug;
        swatch.dataset.vtColor = colorName;
        paintSwatch(swatch, colorName);
    }

    function paintFilterGroup(group) {
        var options = group.querySelectorAll('.fs-checkbox');
        for (var i = 0; i < options.length; i++) paintFilterOption(options[i]);
    }

    /* Typing in the group's search box re-renders the rows without firing serp-filters, so
       the group is watched itself. Scoped to this one group, and every pass is a no-op once
       the rows match (the observer's own insertions settle after one extra pass). Mutation
       callbacks run as microtasks right after Fast Simon's update, so the correct swatch is
       on screen in the same frame as the new text — no flash of the old one. A group Fast
       Simon replaces is picked up again by the next serp-filters, and its observer goes with
       the discarded node. */
    function watchFilterGroup(group) {
        if (group.vtSwatchObserver) return;
        group.vtSwatchObserver = new MutationObserver(function () {
            guard('filter swatches', function () { paintFilterGroup(group); });
        });
        group.vtSwatchObserver.observe(group, { childList: true, characterData: true, subtree: true });
    }

    /* A filter is a colour filter when its id is one of the theme's own colour identifiers
       (products.product.color_swatch_identifiers, "Color,Colour") — the same rule the cards
       use — rather than one hard-coded name, so a renamed or second colour filter gets
       swatches too. Exact token match: "Isp-color-family" and a synced
       "Shopify.color-pattern" filter are not colour-option filters and are left alone. */
    var COLOR_FILTER_IDS = THEME_COLOR_OPTION_IDS.split(',')
        .map(function (token) { return token.trim(); })
        .filter(Boolean);

    function isColorFilter(filterId) {
        return COLOR_FILTER_IDS.indexOf(String(filterId || '').trim().toLowerCase()) !== -1;
    }

    function addFilterSwatches(container) {
        var groups = container.querySelectorAll('[data-filter-id]');
        for (var i = 0; i < groups.length; i++) {
            var group = groups[i];
            if (!isColorFilter(group.getAttribute('data-filter-id'))) continue;

            /* FSStyle lays the swatch rows out from this marker rather than from the
               "Colour" filter's element id. An attribute, not a class: Svelte rewrites the
               class list of elements it renders, but leaves attributes it does not own. */
            if (!group.hasAttribute('data-vt-swatch-filter')) group.setAttribute('data-vt-swatch-filter', '');

            paintFilterGroup(group);
            watchFilterGroup(group);
        }
    }

    /* True only when a variant carries an attribute keyed on a known colour identifier,
       which is the same test live makes on the option name. */
    function hasThemeColorOption(data) {
        return ((data && data.variants) || []).some(function (variant) {
            return Object.keys((variant && variant.attributes) || {}).some(function (key) {
                return THEME_COLOR_OPTION_IDS.indexOf(key.toLowerCase()) !== -1;
            });
        });
    }

    /* Match live: bundles show no colour row.

       Never delete Fast Simon's own .color-swatch-container nodes. Svelte holds references
       to what it rendered and anchors later inserts against them, so removing one makes the
       next update throw "Cannot read properties of null (reading 'insertBefore')" and leaves
       the whole SERP frozen. Hide the row with a data attribute instead and remove only the
       containers this file injected. Adding to a Svelte subtree is safe; removing is not. */
    function clearCardSwatches(productElement) {
        productElement.dataset.vtNoSwatches = '1';

        var injected = productElement.querySelectorAll('.vt-swatch-container');
        for (var i = 0; i < injected.length; i++) injected[i].remove();

        delete productElement.dataset.vtSwatchUrls;
    }

    function cardColors(data) {
        var colors = [];
        ((data && data.variants) || []).forEach(function (variant) {
            var color = getVariantColor(variant);
            if (color && colors.indexOf(color) === -1) colors.push(color);
        });
        return colors;
    }

    function buildSwatchContainer(colorName, selected) {
        var slug = String(colorName).toLowerCase().replaceAll(' ', '_');

        /* vt-swatch-container marks this node as ours, so clearCardSwatches can remove it
           without ever touching one Fast Simon rendered. */
        var container = document.createElement('span');
        container.className = 'color-swatch-container fs-color-swatch-container vt-swatch-container';

        var swatch = document.createElement('span');
        swatch.className = 'color-swatch fast-swatch-color-fallback fast-swatch-color-' + slug +
            ' isp-product-color-swatch-' + slug + (selected ? ' selected' : '');
        swatch.style.setProperty('--swatch-width', '25px');
        swatch.style.setProperty('--swatch-height', '25px');

        var name = document.createElement('span');
        name.className = 'color-swatch-color-name fs-color-name';
        name.textContent = colorName;

        container.appendChild(swatch);
        container.appendChild(name);
        return container;
    }

    /* Paints Fast Simon's own swatch containers where it rendered them, and builds them
       from the variants where it did not (single-colour cards, and every upsell slide).
       Also records the variant PDP each swatch points at. Idempotent. */
    function renderCardSwatches(productElement, data) {
        var swatchesRoot = productElement.querySelector('.color-swatches');
        if (!swatchesRoot) return;

        /* a re-keyed card may be carrying the hidden flag from the product shown before */
        delete productElement.dataset.vtNoSwatches;

        var colors = cardColors(data);
        if (!productElement.querySelector('.color-swatch-container')) {
            colors.forEach(function (color, index) {
                swatchesRoot.appendChild(buildSwatchContainer(color, index === 0));
            });
        }

        var containers = productElement.querySelectorAll('.color-swatch-container');
        /* Fast Simon re-keys a card to another product on re-render, wiping its swatches but
           leaving this attribute behind. */
        if (!containers.length) {
            delete productElement.dataset.vtSwatchUrls;
            return;
        }

        var variants = (data && data.variants) || [];
        var urls = {};

        for (var i = 0; i < containers.length; i++) {
            var nameEl = containers[i].querySelector('.color-swatch-color-name');
            var colorName = nameEl && nameEl.textContent.trim();
            if (!colorName) continue;

            paintSwatch(containers[i].querySelector('.color-swatch'), colorName);

            if (data && data.productURL) {
                var variant = variants.find(function (v) { return getVariantColor(v) === colorName; });
                /* With a filter applied Fast Simon's productURL already carries the matching
                   variant, so the swatch's own variant replaces that query instead of being
                   appended as a second one, which the PDP ignores. */
                urls[colorName] = variant
                    ? data.productURL.split('?')[0] + '?variant=' + variant.id
                    : data.productURL;
            }
        }

        productElement.dataset.vtSwatchUrls = JSON.stringify(urls);
    }

    /* Live's swatches link to the variant PDP; Fast Simon's swap the card image instead.
       Delegated from the root because Fast Simon rebuilds these nodes on every render. */
    function installSwatchNavigation(root) {
        if (root.vtSwatchNavigation) return;
        root.vtSwatchNavigation = true;

        function swatchUrl(event) {
            var container = event.target.closest && event.target.closest('.color-swatch-container');
            if (!container) return null;

            var card = container.closest(CARD_SELECTOR);
            if (!card || !card.dataset.vtSwatchUrls) return null;

            var nameEl = container.querySelector('.color-swatch-color-name');
            if (!nameEl) return null;

            var urls;
            try { urls = JSON.parse(card.dataset.vtSwatchUrls); } catch (error) { return null; }
            return urls[nameEl.textContent.trim()] || null;
        }

        function goToVariant(event) {
            var url = swatchUrl(event);
            if (!url) return;

            event.preventDefault();
            event.stopPropagation();
            window.location.assign(url);
        }

        root.addEventListener('click', goToVariant, true);

        /* Touch does not: Fast Simon calls preventDefault() on touchend, so the browser
           never synthesises a click and the tap never reaches the listener above. pointerup
           carries it regardless, guarded as a real tap so a scroll that happens to lift off
           over a swatch does not navigate. */
        var TAP_SLOP_PX = 10;
        var tapStart = null;

        root.addEventListener('pointerdown', function (event) {
            var container = event.target.closest && event.target.closest('.color-swatch-container');
            tapStart = container ? { container: container, x: event.clientX, y: event.clientY } : null;
        }, true);

        root.addEventListener('pointerup', function (event) {
            var start = tapStart;
            tapStart = null;

            /* mouse is already covered by click, and going early would break drag-off-to-cancel */
            if (event.pointerType === 'mouse' || !start) return;
            if (Math.abs(event.clientX - start.x) > TAP_SLOP_PX) return;
            if (Math.abs(event.clientY - start.y) > TAP_SLOP_PX) return;

            var container = event.target.closest && event.target.closest('.color-swatch-container');
            if (container !== start.container) return;

            goToVariant(event);
        }, true);

        root.addEventListener('pointercancel', function () { tapStart = null; }, true);
    }

    /* End Colour Swatches (pattern files) */

    /* Start Filters Modal */

    /* "82 results" -> "82", for the Apply button badge. With content search on, the mobile
       layout drops that line and shows "Products (82)" / "Articles (2)" tabs instead, so the
       Products tab's count is the fallback. */
    function getTotalResultsCount(root) {
        var el = root.querySelector('.fs-total-results-text');
        var match = el && el.textContent.match(/[\d,]+/);
        return match ? match[0] : productsTabCount(root);
    }

    /* Products is always the first tab; Articles follows it. */
    function productsTabCount(root) {
        var label = root.querySelector('.top-center-container .tabs-wrapper .fs-single-tab:first-child .fs-single-tab-label');
        var match = label && label.textContent.match(/\(\s*([\d,]+)\s*\)/);
        return match ? match[1] : '';
    }

    /* Mobile + content search: the Products tab is restyled in FSStyle into the "N results"
       line it replaced. The words come through a custom property, never by rewriting the
       tab's text — Svelte owns that node, and a rewritten one stops updating. The Apply
       badge is refreshed here too, so it follows the count after every render. */
    function syncResultsLine(root) {
        var tabs = root.querySelector('.top-center-container .tabs-wrapper');
        if (tabs) {
            var count = productsTabCount(root);
            var text = count ? count + (count === '1' ? ' result' : ' results') : '';
            if (text) {
                var value = JSON.stringify(text);
                if (tabs.style.getPropertyValue('--vt-results-text') !== value) tabs.style.setProperty('--vt-results-text', value);
                if (!tabs.hasAttribute('data-vt-results')) tabs.setAttribute('data-vt-results', '');
            } else if (tabs.hasAttribute('data-vt-results')) {
                tabs.style.removeProperty('--vt-results-text');
                tabs.removeAttribute('data-vt-results');
            }
        }

        /* our own node, so writing its text is safe; only when it differs, so it can never
           feed a loop if the modal ever sits inside the observed bar */
        var badge = root.querySelector('.vt-filters-count');
        var total = getTotalResultsCount(root);
        if (badge && badge.textContent !== total) badge.textContent = total;
    }

    /* Hooks fire before Svelte commits, so the count read during one can be the previous
       render's. Watch the top bar itself — a small subtree — and resync after each commit.
       Fast Simon rebuilds the bar when it switches layout, so every hook call re-checks
       which node is current. */
    function watchResultsLine(root) {
        if (!root || !root.querySelector) return;
        var bar = root.querySelector('.top-center-container');
        if (!bar) return;
        if (bar.vtResultsObserver) { syncResultsLine(root); return; }

        bar.vtResultsObserver = new MutationObserver(function () {
            guard('results line', function () { syncResultsLine(root); });
        });
        bar.vtResultsObserver.observe(bar, { childList: true, characterData: true, subtree: true });
        syncResultsLine(root);
    }
    /* Fast Simon re-reads the URL on popstate, so pushing the clean URL and firing one
       clears every filter in place, with no page load. The search term and the sort are
       kept; the filters and the page number go. */
    var CLEAR_ALL_KEPT_PARAMS = ['q', 'sort'];

    function clearAllFilters() {
        var current = new URLSearchParams(window.location.search);
        var kept = new URLSearchParams();
        CLEAR_ALL_KEPT_PARAMS.forEach(function (key) {
            if (current.get(key)) kept.set(key, current.get(key));
        });
        var search = kept.toString();
        var url = window.location.pathname + (search ? '?' + search : '');
        if (url === window.location.pathname + window.location.search) return;

        window.history.pushState(null, '', url);
        window.dispatchEvent(new PopStateEvent('popstate', { state: null }));
    }

    /* Apply / Clear all footer, pinned to the bottom of the modal like the live theme. */
    function addFiltersFooter(container) {
        var modal = container.closest('.filters-modal');
        if (!modal) return;

        var root = container.getRootNode();
        var footer = modal.querySelector('.vt-filters-footer');

        if (!footer) {
            footer = document.createElement('footer');
            footer.className = 'vt-filters-footer';
            footer.innerHTML =
                '<a href="#" role="button" class="vt-filters-apply">Apply <span class="vt-filters-count"></span></a>' +
                '<a href="#" role="button" class="vt-filters-clear">Clear all</a>';

            footer.querySelector('.vt-filters-apply').addEventListener('click', function (event) {
                event.preventDefault();
                var close = root.querySelector('.fs-close-filters-button');
                if (close) close.click();
            });

            footer.querySelector('.vt-filters-clear').addEventListener('click', function (event) {
                event.preventDefault();
                clearAllFilters();
            });

            modal.appendChild(footer);
        }

        footer.querySelector('.vt-filters-count').textContent = getTotalResultsCount(root);
    }

    /* End Filters Modal */

    /* Start Product Cards */

    /* Reuse the theme's quick view: component-quick-buy.js needs only a handle and an id,
       and is document-scoped, so it works from inside the shadow root.

       Do not gate on customElements.get('quick-buy') — the theme loads that script lazily
       and it is rarely defined this early. An element parsed into the DOM upgrades itself
       when define() lands, with its <a> child already in place. */
    /* Live shows quick buy only where the device can hover: component-card.css reveals it
       under @media (hover:hover), so phones and touch tablets never see it at any width.
       Gated on the same capability rather than on viewport width — a width test kept it
       on an iPad in landscape and dropped it from a narrow desktop window, both unlike
       live. FSStyle/FSUpsellStyle gate the reveal the same way, which also covers a device
       whose hover capability changes after the cards were drawn. */
    function canHover() {
        return !window.matchMedia || window.matchMedia('(hover: hover)').matches;
    }

    function addThemeQuickBuy(productElement, data) {
        if (!canHover()) return;

        var wrapper = productElement.querySelector('.image-wrapper');
        var url = data && data.productURL;
        if (!wrapper || !url) return;

        /* Presence-check the node, not a dataset flag: Fast Simon keeps the card across
           updates but rebuilds the image wrapper, dropping this node while a flag would
           survive and wrongly suppress re-injection. */
        if (wrapper.querySelector('quick-buy')) return;

        var handle = url.split('/products/')[1];
        if (!handle) return;
        handle = handle.split('?')[0];

        /* insertAdjacentHTML so the <a> exists when the custom element upgrades */
        wrapper.insertAdjacentHTML('beforeend', QUICK_BUY_TEMPLATE
            .replace(/__ID__/g, cardProductId(productElement) || data.id)
            .replace('__HANDLE__', handle)
            .replace('__URL__', url));
    }

    function decorateProductCard(productElement, data) {
        if (!data) return;

        /* Carried as a custom property rather than an injected <img>, to leave Svelte alone. */
        if (data.imageURL2) {
            productElement.style.setProperty('--vt-hover-image', 'url("' + data.imageURL2 + '")');
            productElement.style.setProperty('--vt-hover-image-opacity', '1');
        }

        /* The feed leaves product.vendor undefined; the real value is in attributes. */
        var vendor = getProductAttribute(data, 'Vendor');
        if (vendor) productElement.style.setProperty('--vt-vendor', JSON.stringify(vendor));

        /* Live centres the struck compare price and the sale price as one run; Fast Simon
           puts them in separate grid columns. Hand the text to CSS, which hides Fast Simon's
           element and prints this instead. From the payload, not the DOM — the hook can run
           ahead of the render. */
        if (data.formattedCompare) {
            productElement.style.setProperty('--vt-compare', JSON.stringify(data.formattedCompare));
        } else {
            productElement.style.removeProperty('--vt-compare');
        }

        if (hasThemeColorOption(data)) {
            renderCardSwatches(productElement, data);
        } else {
            /* Match live: bundle/pack products get no swatch row. */
            clearCardSwatches(productElement);
        }

        addThemeQuickBuy(productElement, data);
    }

    /* End Product Cards */

    /* Start Wishlist (HulkApps Advanced Wishlist) */

    /* The Hulk app scans the light DOM and listens on document, so it never sees a card
       inside the shadow root. The heart is drawn here and the add/remove handed straight
       back to the app's own module, so its cookie, toast, GA4 and picker keep working. */

    /* Inline SVG: the app's icon font is declared in a light-DOM stylesheet that does not
       reach the shadow root. */
    var WISHLIST_HEART_PATH =
        'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z';

    /* Everything below no-ops until the app has booted. */
    function wishlistApp() {
        var collection = window.hulkappsWishlistCollection;
        var core = window.HulkappWishlist;
        var config = window.hulkappsWishlist;
        if (!collection || !core || !config) return null;
        if (typeof collection._manageWishlistProducts !== 'function') return null;
        return { collection: collection, core: core, config: config };
    }

    function wishlistLanguage() {
        var settings = (window.hulkappsWishlist || {}).languageSettings || {};
        var primary = Object.keys(settings).find(function (key) {
            return settings[key] && settings[key].primary_language === true;
        });
        return settings[window.Shopify && window.Shopify.locale] || settings[primary] || {};
    }

    /* Size and colours come from the app's own Button style settings, set once per render
       and inherited by every button. The collection_* values dress the icon-only heart the
       upsell slides still use; the button_* ones are the app's own product-button style,
       which is what the PLP card's "Add to wishlist" bar is drawn from — so that bar keeps
       being editable in the Wishlist app's Button style screen rather than here. */
    function setWishlistTheme(element) {
        var style = (window.hulkappsWishlist || {}).buttonStyleSettings || {};
        element.style.setProperty('--vt-wishlist-size', (style.collection_icon_size || 20) + 'px');
        element.style.setProperty('--vt-wishlist-color', style.collection_icon_fill_color || '#CD4D1E');
        element.style.setProperty('--vt-wishlist-color-added', style.collection_selected_icon_fill_color || '#6D7866');
        element.style.setProperty('--vt-wishlist-color-hover', style.collection_icon_hover_color || '#6D7866');

        element.style.setProperty('--vt-wishlist-btn-bg', style.button_background_color || '#F47645');
        element.style.setProperty('--vt-wishlist-btn-color', style.button_text_color || '#FFFFFF');
        element.style.setProperty('--vt-wishlist-btn-bg-hover', style.hover_button_background_color || '#FFFFFF');
        element.style.setProperty('--vt-wishlist-btn-color-hover', style.hover_button_text_color || '#CD4D1E');
        element.style.setProperty('--vt-wishlist-btn-border-color',
            style.hover_button_border_color || style.button_border_color || '#333');
        element.style.setProperty('--vt-wishlist-btn-border-width', (style.button_border || 0) + 'px');
        element.style.setProperty('--vt-wishlist-btn-radius', (style.button_border_radius || 0) + 'px');
        element.style.setProperty('--vt-wishlist-btn-font-size', (style.button_font_size || 14) + 'px');
        element.style.setProperty('--vt-wishlist-btn-font-weight', style.button_font_weight || 500);
        element.style.setProperty('--vt-wishlist-btn-padding-y', (style.button_top_bottom_vertical_space || 9) + 'px');
        element.style.setProperty('--vt-wishlist-btn-padding-x', (style.button_left_right_vertical_space || 16) + 'px');
    }

    function productHandle(productElement, data) {
        var link = productElement.querySelector('a.fs-product-main-image-wrapper, a.title-container');
        var url = (link && link.getAttribute('href')) || (data && data.productURL) || '';
        return (url.split('/products/')[1] || '').split(/[?#]/)[0] || null;
    }

    function appendWishlistLabel(button, className, text, name) {
        var label = document.createElement('span');
        label.className = 'vt-wishlist-label ' + className;
        label.textContent = text;

        /* The app's wording ("Add to wishlist") is the same on every card, so the product
           name is what makes each button's accessible name unique in a grid. It stays
           hidden in both placements — the PLP bar shows the app's wording alone, exactly as
           the app's own button does on a live theme card. */
        var context = document.createElement('span');
        context.className = 'vt-wishlist-label-product';
        context.textContent = ' ' + name;
        label.appendChild(context);

        button.appendChild(label);
    }

    /* Where the button goes: under the card's info block, which is where the app's own
       "Add to wishlist" button sits on a live theme card, and what frees the image for
       badges. PLP cards and upsell slides share the same .info-container grid. The image
       wrapper is only a fallback for a card that has no info block. */
    function wishlistButtonTarget(productElement) {
        var info = productElement.querySelector(
            '.fs-serp-info, .fs-recommendation-product-info, .info-container');
        if (info) return info;

        /* .image-wrapper is the upsell slide's image box; the PLP grid carries both that
           and its own fs-serp-* names, list view only the latter. */
        return productElement.querySelector(
            '.fs-serp-image-wrapper, .fs-serp-image-wrapper-list-view, .image-wrapper');
    }

    /* One button per card. Guarded on the card, not on the target, so a card that already
       carries one cannot gain a second from the other placement. */
    function addWishlistButton(productElement, data) {
        if (!wishlistApp()) return;

        var wrapper = wishlistButtonTarget(productElement);
        if (!wrapper) return;

        var productId = cardProductId(productElement);
        var handle = productHandle(productElement, data);
        if (!productId && !handle) return;

        /* Fast Simon can re-key a card (and a carousel can reuse a slide) for another product
           while keeping the node the button lives in. A button that still names the previous
           product would add the wrong item, so it is rebuilt; it is our node, so removing it
           is safe. The status pass scheduled after every render repaints its state. */
        var existing = productElement.querySelector('.vt-wishlist-btn');
        if (existing) {
            var sameProduct = productId
                ? existing.dataset.proid === String(productId)
                : existing.dataset.prohandle === handle;
            if (sameProduct) return;
            existing.remove();
        }

        var titleElement = productElement.querySelector('.fs-product-title, .title-container');
        var name = (data && data.title) || (titleElement && titleElement.textContent.trim()) || 'this product';

        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'vt-wishlist-btn';
        /* the attribute the app's own status updater looks for */
        button.setAttribute('data-gridwlhulklist', '');
        button.setAttribute('data-added', 'false');
        if (productId) button.dataset.proid = productId;
        if (handle) button.dataset.prohandle = handle;
        button.innerHTML =
            '<svg class="vt-wishlist-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
            '<path d="' + WISHLIST_HEART_PATH + '"></path></svg>';

        /* Both labels are rendered; CSS exposes only the one matching data-added, so both
           the visible text and the accessible name follow the state with no JS. The wording
           is the app's own, so the Wishlist app's Language screen keeps owning it. */
        var language = wishlistLanguage();
        appendWishlistLabel(button, 'vt-wishlist-label-add', language.button_text || 'Add to wishlist', name);
        appendWishlistLabel(button, 'vt-wishlist-label-remove', language.success_button_text || 'Added to wishlist', name);

        wrapper.appendChild(button);
    }

    /* _collectionProductStatus() reads the global wishlistBtns, so publish ours there,
       merged with any light-DOM ones. A page can hold the PLP root and several widget
       roots at once, so every root seen so far is republished together. */
    var wishlistRoots = [];
    function registerWishlistButtons(root) {
        if (root && wishlistRoots.indexOf(root) === -1) wishlistRoots.push(root);

        var buttons = [].slice.call(document.querySelectorAll('[data-gridwlhulklist]'));
        wishlistRoots.forEach(function (knownRoot) {
            buttons = buttons.concat([].slice.call(knownRoot.querySelectorAll('.vt-wishlist-btn')));
        });

        window.wishlistBtns = buttons;
        return buttons.length;
    }

    /* One status request per page, debounced so a burst of renders collapses into one call. */
    var wishlistStatusTimer = null;
    function scheduleWishlistStatus(root) {
        var app = wishlistApp();
        if (!app) return;
        if (wishlistStatusTimer) clearTimeout(wishlistStatusTimer);
        wishlistStatusTimer = setTimeout(function () {
            wishlistStatusTimer = null;
            if (registerWishlistButtons(root)) app.collection._collectionProductStatus();
        }, 250);
    }

    /* The picker modal is in the light DOM and its confirm button reads collectionWlBtnTarget,
       so open it the way the app's own handler does and let the app finish. */
    function openWishlistPicker(button) {
        var app = wishlistApp();
        var search = document.querySelector('[data-wishlistsearch]');
        var popup = document.querySelector('[data-hulkappsWishlistPopup]');
        var confirmButton = popup && popup.querySelector('.wishlist-modal__footer button');
        if (!app || !window.hulkModalManager || !search || !confirmButton) return;

        window.hulkModalManager._show('myWishlistsPopup');
        document.body.classList.add('popup__active');
        window.searchWishlist = '';
        search.value = '';
        search.setAttribute('data-hulkcollection', button.dataset.proid);
        app.core._getUserWishlist(true);
        app.collection._updateWishlistSelection(button.dataset.proid);
        window.collectionWlBtnTarget = button;
        confirmButton.removeAttribute('data-addtowishlist');
        confirmButton.setAttribute('data-addproducttowishlist', '');
    }

    /* Delegated from the shadow root: Fast Simon rebuilds these nodes on every render. */
    function installWishlistClicks(root) {
        if (root.vtWishlistClicks) return;
        root.vtWishlistClicks = true;

        function toggleWishlist(event) {
            var button = event.target.closest && event.target.closest('.vt-wishlist-btn');
            if (!button) return;

            /* the card and its slide are links; the button must not navigate */
            event.preventDefault();
            event.stopPropagation();

            var app = wishlistApp();
            if (!app || button.dataset.vtWishlistBusy) return;

            /* Claimed before any branch: on touch this runs from pointerup, and the
               compatibility click Android sends afterwards must not toggle it back, reopen
               the picker or repeat the guest message. */
            button.dataset.vtWishlistBusy = '1';
            setTimeout(function () { delete button.dataset.vtWishlistBusy; }, 600);

            var general = app.config.generalSettings || {};
            var isGuest = !app.config.customerID;

            if (isGuest && !general.is_guest_enabled) {
                app.core._messageUpdate((wishlistLanguage().success_message || {}).for_guest_login_disabled);
                return;
            }

            if (!isGuest && general.has_multiple_wishlist) {
                openWishlistPicker(button);
                return;
            }

            /* re-publish first: the app re-reads wishlistBtns to repaint after the write */
            registerWishlistButtons(root);

            app.collection._manageWishlistProducts(
                button,
                button.dataset.proid,
                isGuest ? '' : window.defaultWishlistID,
                button.dataset.prohandle
            );
        }

        root.addEventListener('click', toggleWishlist, true);

        /* Two taps on touch: the first tap is what applies the card's :hover (Fast Simon's
           second-image swap, the image zoom), and iOS withholds the click when that first tap
           changes what is rendered. pointerup is never withheld, so the tap is carried on it,
           guarded as a real tap (same shape as the swatch handler) so a scroll that happens to
           lift off over the button does not toggle it. */
        var WISHLIST_TAP_SLOP_PX = 10;
        var wishlistTapStart = null;

        root.addEventListener('pointerdown', function (event) {
            var button = event.target.closest && event.target.closest('.vt-wishlist-btn');
            wishlistTapStart = button ? { button: button, x: event.clientX, y: event.clientY } : null;
        }, true);

        root.addEventListener('pointerup', function (event) {
            var start = wishlistTapStart;
            wishlistTapStart = null;

            /* mouse is already covered by click, and going early would break drag-off-to-cancel */
            if (event.pointerType === 'mouse' || !start) return;
            if (Math.abs(event.clientX - start.x) > WISHLIST_TAP_SLOP_PX) return;
            if (Math.abs(event.clientY - start.y) > WISHLIST_TAP_SLOP_PX) return;

            var button = event.target.closest && event.target.closest('.vt-wishlist-btn');
            if (button !== start.button) return;

            toggleWishlist(event);
        }, true);

        root.addEventListener('pointercancel', function () { wishlistTapStart = null; }, true);
    }

    /* End Wishlist (HulkApps Advanced Wishlist) */    

    /* Start Autocomplete */

    /* Products and the summary bar navigate, which takes the drawer with them, but a
       recent search or suggestion is handled in place and left the drawer sitting open over
       the results. Live has no such state — every row there is a link. */
    function closeSearchDrawer() {
        var drawer = document.querySelector('#drawer-search');
        /* .is-open is the real state — the theme leaves display:block on the closed drawer
           too, so a display test never bails and would click [data-close] on every render. */
        if (!drawer || !drawer.classList.contains('is-open')) return;

        /* the theme's own close control, so its focus handling and overlay run as usual */
        var close = drawer.querySelector('[data-close]');
        if (close) { close.click(); return; }

        var modals = window.Woolman && window.Woolman.ModalsAndDrawers;
        if (modals && typeof modals.closeModalOrDrawer === 'function') modals.closeModalOrDrawer(drawer);
    }

    /* Submitting the drawer's own search is the third in-place case. Fast Simon intercepts
       the Enter keydown itself, so no submit event is ever dispatched and there is nothing
       to listen to on the form — results-rendered is the only signal. On live the submit is
       a real navigation, so the drawer goes with the page; here it was left open over the
       new results.

       results-rendered also fires on filter, sort and paginate, so the close is tied to the
       drawer's own term: it only runs when the drawer is open AND its input holds the term
       the results were just rendered for. That keeps a first render that happens to land
       while the drawer is open from snapping it shut. */
    function closeSearchDrawerAfterSubmit() {
        var drawer = document.querySelector('#drawer-search');
        if (!drawer || !drawer.classList.contains('is-open')) return;

        var input = drawer.querySelector('#drawerSearchInput');
        var term = ((input && input.value) || '').trim();
        if (!term) return;

        var query;
        try { query = new URLSearchParams(window.location.search).get('q') || ''; }
        catch (error) { return; }

        if (query.trim() === term) closeSearchDrawer();
    }

    /* Delegated from the dropdown's shadow root: Fast Simon rebuilds these rows on every
       keystroke, so a listener bound to a row would not survive the next one. */
    function installAutocompleteClose(root) {
        if (!root || root.vtAutocompleteClose) return;
        root.vtAutocompleteClose = true;

        root.addEventListener('click', function (event) {
            if (!event.target.closest) return;
            if (!event.target.closest('.fast-ac-item, .summery')) return;
            /* let Fast Simon's own handler run first, then take the drawer down */
            setTimeout(closeSearchDrawer, 0);
        }, true);
    }

    function autocompleteHooks() {
        var autocomplete = window.FastAutocomplete;
        if (!autocomplete || !autocomplete.registerHook) return;
        if (window.vtAutocompleteHooked) return;
        window.vtAutocompleteHooked = true;

        /* Each row hook hands back the element it rendered — the cheapest way to reach the
           dropdown's shadow root without scanning for it. */
        ['render-autocomplete-recent-item',
         'render-autocomplete-popular-search-item',
         'render-autocomplete-category-item',
         'render-autocomplete-turbolink-item',
         'render-autocomplete-product-item',
         'render-autocomplete-summary'].forEach(function (hook) {
            autocomplete.registerHook(hook, function (payload) {
                var element = payload && payload.element;
                if (element && element.getRootNode) installAutocompleteClose(element.getRootNode());
            });
        });
    }

    /* End Autocomplete */

    /* Start Search Results Page */

    /* /pages/search-results is a page, not the /search route, so Liquid's `search` object
       is empty there and the heading and input render blank. The term is in the URL. */
    function syncSearchResultsPage() {
        var heading = document.querySelector('#collection-ajax .pt-40 h1');
        var input = document.getElementById('PageSearch');
        if (!heading && !input) return;

        var term = '';
        try { term = new URLSearchParams(window.location.search).get('q') || ''; } catch (error) { return; }

        /* keeps the in-page input in step with whatever was searched from the drawer */
        if (input && input.value !== term) input.value = term;

        if (!heading) return;
        var text = term ? SEARCH_FOR_LABEL.replace('__TERM__', term) : SEARCH_LABEL;
        if (heading.textContent !== text) heading.textContent = text;
    }

    /* End Search Results Page */

    /* Start Upsell / Cross-Sell Widgets */

    /* The widget renders no vendor row, so one is added as a sibling above the info block.
       Deliberately not a grid item inside it: that grid is laid out from an inline
       --product-info-grid-areas Fast Simon owns, and a row added there lands in the wrong
       cell whenever that value or the shadow-root stylesheet arrives late.
       Rewritten rather than skipped, so a reused slide never keeps the previous brand. */
    function renderUpsellVendor(slide, data) {
        var info = slide.querySelector('.fs-recommendation-product-info');
        if (!info) return;

        var node = slide.querySelector('.vt-upsell-vendor');
        var vendor = getProductAttribute(data, 'Vendor') || (data && data.vendor);

        /* No brand for this product: drop any node a previously shown product left behind,
           which also hands the info block back its own 24px top padding. */
        if (!vendor || vendor === 'undefined') {
            if (node) node.remove();
            return;
        }

        if (!node) {
            node = document.createElement('span');
            node.className = 'vt-upsell-vendor';
            info.parentNode.insertBefore(node, info);
        }
        if (node.textContent !== vendor) node.textContent = vendor;
    }

    function decorateUpsellSlide(slide, data) {
        if (!data) return;

        renderUpsellVendor(slide, data);

        if (hasThemeColorOption(data)) {
            renderCardSwatches(slide, data);
        } else {
            /* Match live: bundle/pack products get no swatch row. */
            clearCardSwatches(slide);
        }

        addThemeQuickBuy(slide, data);
        addWishlistButton(slide, data);
    }

    /* Only "default"-styled widgets are matched to the live card. The mini-cart widget has
       its own style and card, and the stylesheet is scoped away from it too. */
    var UPSELL_STYLE_SELECTOR = '.fs-widget-style-default-style';

    function decorateUpsellWidget(container, products) {
        if (!container || !products) return;
        if (!container.closest(UPSELL_STYLE_SELECTOR)) return;

        var root = container.getRootNode();
        installSwatchNavigation(root);
        installWishlistClicks(root);
        setWishlistTheme(container);

        var slides = container.querySelectorAll('.fs-upsell-cross-sell-product');
        for (var i = 0; i < slides.length; i++) {
            (function (slide) {
                guard('upsell slide ' + cardProductId(slide), function () {
                    decorateUpsellSlide(slide, products[cardProductId(slide)]);
                });
            })(slides[i]);
        }
        scheduleWishlistStatus(root);
    }

    /* The products hook fires once and carousel navigation does not re-fire it, so the
       payload is kept and re-applied by a debounced observer scoped to this one widget.
       Every decoration is idempotent, so a spurious pass costs one querySelectorAll. */
    var UPSELL_REDECORATE_DELAY = 250;

    function observeUpsellWidget(container, products) {
        if (container.vtUpsellObserved) return;
        container.vtUpsellObserved = true;

        var timer = null;
        var observer = new MutationObserver(function () {
            if (timer) clearTimeout(timer);
            timer = setTimeout(function () {
                timer = null;
                observer.disconnect();
                guard('upsell re-decorate', function () { decorateUpsellWidget(container, products); });
                observer.observe(container, { childList: true, subtree: true });
            }, UPSELL_REDECORATE_DELAY);
        });
        observer.observe(container, { childList: true, subtree: true });
    }

    function upsellHooks() {
        if (!window.FastRecommendationWidget || !window.FastRecommendationWidget.registerHook) return;
        if (window.vtUpsellHooked) return;
        window.vtUpsellHooked = true;

        window.FastRecommendationWidget.registerHook('upsell-cross-sell-products', function (payload) {
            var container = payload && payload.element;
            var products = payload && payload.products;
            if (!container || !products) return;
            /* checked here too, so no observer is ever attached to a mini-cart widget */
            if (!container.closest(UPSELL_STYLE_SELECTOR)) return;

            guard('upsell-cross-sell-products', function () {
                decorateUpsellWidget(container, products);
                observeUpsellWidget(container, products);
            });
        });
    }

    /* End Upsell / Cross-Sell Widgets */

    function hooks()
    {
        /* fast-serp-ready can fire more than once; without this every hook registers again
           and each card is decorated twice per render. */
        if (window.vtSerpHooked) return;
        window.vtSerpHooked = true;

        SerpOptions.registerHook('serp-filters', ({facets, element}) => {
            guard('serp-filters', function () {
                addFilterSwatches(element);
                addFiltersFooter(element);
                watchResultsLine(element.getRootNode());
            });
        });

        SerpOptions.registerHook('serp-product-grid', ({products, element}) => {
            guard('serp-product-grid', function () {
                const root = element.getRootNode();
                installSwatchNavigation(root);
                installWishlistClicks(root);
                setWishlistTheme(element);

                for (const productElement of getProductElements(element)) {
                    const data = products[productElement.dataset.productId];
                    /* per card, so one unusual product cannot stop the cards after it from
                       being decorated — that is what left later swatches unpainted */
                    guard('card ' + productElement.dataset.productId, function () {
                        decorateProductCard(productElement, data);
                        addWishlistButton(productElement, data);
                    });
                }
                scheduleWishlistStatus(root);
                watchResultsLine(root);
            });
        });
    }

    // execution here
    if (window.SerpOptions) {
        hooks();
    } else {
       window.addEventListener('fast-serp-ready', function () {
           hooks();
       });
    }

    /* The hook only fires for widgets rendered after it is registered, and the bundle loads
       async — so latch onto the global the moment the app publishes it. The ready events are
       the documented path; whichever lands first wins and the rest are no-ops. */
    function bootUpsellHooks() {
        if (window.FastRecommendationWidget) { upsellHooks(); return; }
        try {
            var pending;
            Object.defineProperty(window, 'FastRecommendationWidget', {
                configurable: true,
                get: function () { return pending; },
                set: function (value) { pending = value; upsellHooks(); }
            });
        } catch (error) { /* another script owns the property; the ready events still cover us */ }
    }

    bootUpsellHooks();
    window.addEventListener('fast-upsell-cross-sell-ready', upsellHooks);
    window.addEventListener('fast-upsell-crossell-ready', upsellHooks);

    /* The autocomplete only has to be hooked before the first row renders, which cannot
       happen before the input is ready. */
    autocompleteHooks();
    window.addEventListener('fast-autocomplete-ready', autocompleteHooks);

    /* Runs in <head>, so the markup is not parsed yet on the first call.
       results-rendered covers a term changed without a page load. */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', syncSearchResultsPage);
    } else {
        syncSearchResultsPage();
    }
    window.addEventListener('popstate', syncSearchResultsPage);
    window.addEventListener('fs-custom-events-results-rendered', syncSearchResultsPage);
    window.addEventListener('fs-custom-events-results-rendered', closeSearchDrawerAfterSubmit);

    //end for Vnext

})();
